/**
 * A Plan Line's resourcing status — Open, Proposed or Confirmed.
 *
 * DERIVED, not stored. The two facts it reads already live on the line: who is named
 * (`suggestedResourceId`) and whether that name is confirmed (`suggestedResourceConfirmed`).
 * A third, independent status column would let the two disagree — a "Confirmed" line with
 * nobody on it — which is exactly what `plan_line_confirmed_requires_resource` exists to
 * forbid. So the status is a reading of those two columns, and setting it writes them.
 *
 *   Open       nobody named yet
 *   Proposed   someone named, not yet confirmed — the default the moment a Resource is
 *              assigned
 *   Confirmed  that person is confirmed as who the plan proposes
 *
 * None of the three books capacity or puts anyone on the Pod by itself. Setting the plan's
 * Resourcing status to Staffed — allowed only once every line is Confirmed — is what writes
 * the confirmed Resources onto the Solution (product decision 2026-10-06).
 */

export const PLAN_LINE_STATUSES = ["Open", "Proposed", "Confirmed"] as const;

export type PlanLineStatus = (typeof PLAN_LINE_STATUSES)[number];

interface LineStaffing {
  suggestedResourceId: string | null;
  suggestedResourceConfirmed: boolean;
  /** RP-24 — a split line's Shares; two or more means the line is split. */
  shares?: readonly unknown[];
  shareBalance?: { unallocatedHours: string; overHours: string };
}

/** RP-24 — whether the line is split across Resources (its Shares say who is on it). */
export function isSplitLine(line: Pick<LineStaffing, "shares">): boolean {
  return (line.shares?.length ?? 0) >= 2;
}

/**
 * A SPLIT line (RP-24) is Confirmed when its Shares cover every hour of the line — an RM
 * put each of them there — and Proposed while any hour is unallocated or over-allocated,
 * which is also what refuses Staffed.
 */
export function planLineStatus(line: LineStaffing): PlanLineStatus {
  if (isSplitLine(line)) {
    const balanced =
      Number(line.shareBalance?.unallocatedHours ?? "0") === 0 &&
      Number(line.shareBalance?.overHours ?? "0") === 0;
    return balanced ? "Confirmed" : "Proposed";
  }
  if (line.suggestedResourceId === null) return "Open";
  return line.suggestedResourceConfirmed ? "Confirmed" : "Proposed";
}

/**
 * The write a status change becomes, or `"pick"` when the line needs a Resource first —
 * Proposed and Confirmed are statements about a specific person, so an Open line cannot
 * reach either without someone being chosen.
 */
export function planLineStatusWrite(
  line: LineStaffing,
  to: PlanLineStatus,
): { resourceId: string | null; confirmed: boolean } | "pick" | null {
  // A split line's status follows its Shares; there is no single Resource to write.
  if (isSplitLine(line)) return null;
  if (planLineStatus(line) === to) return null;
  if (to === "Open") return { resourceId: null, confirmed: false };
  if (line.suggestedResourceId === null) return "pick";
  return { resourceId: line.suggestedResourceId, confirmed: to === "Confirmed" };
}
